import { createFileRoute, redirect, Link, isRedirect } from "@tanstack/react-router";
import * as React from "react";
import { Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "~/lib/auth-client";
import { Skeleton } from "@alchema/ui";
import { useResumeStore } from "~/stores/resume-store";
import { useJobStore } from "~/stores/job-store";
import { orpc } from "~/lib/orpc-client";
import { client } from "~/lib/orpc-client";
import { queryClient } from "~/lib/query-client";
import * as m from "~/paraglide/messages";
import { ReferralWidget } from "~/components/ReferralWidget";
import { requireAuth } from "~/lib/auth-guard";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { SparklesIcon, PlusIcon, BriefcaseIcon } from "~/components/dashboard/DashboardIcons";
import { StatStrip } from "~/components/dashboard/StatStrip";
import { ActivityTable, type ActivityItem } from "~/components/dashboard/ActivityTable";
import { NextSteps } from "~/components/dashboard/NextSteps";

export const Route = createFileRoute("/dashboard")({
  beforeLoad: async () => {
    if (typeof window !== "undefined") {
      try {
        await requireAuth();
      } catch (e: unknown) {
        // Only redirect to login if we got a definitive "no session" response
        // Network errors should not kick the user out
        if (isRedirect(e)) throw e;
        // Session check failed (network error) — let the page render, useSession will handle it
        return;
      }
      try {
        const status = await client.user.getOnboardingStatus();
        if (status && !status.complete) {
          throw redirect({ to: "/onboarding" });
        }
      } catch (e: unknown) {
        if (isRedirect(e)) throw e;
      }
    }
  },
  loader: async () => {
    // Prefetch all dashboard data in parallel during navigation.
    await Promise.all([
      queryClient.prefetchQuery(orpc.billing.currentTier.queryOptions()),
      queryClient.prefetchQuery(orpc.resume.list.queryOptions()),
      queryClient.prefetchQuery(orpc.job.list.queryOptions()),
      queryClient.prefetchQuery(orpc.application.list.queryOptions()),
    ]);
    return null;
  },
  component: DashboardPage,
  pendingComponent: DashboardSkeleton,
  errorComponent: RouteErrorBoundary,
});

function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-5xl" aria-busy="true" role="status" aria-label="Loading dashboard">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="mt-2 h-4 w-72" />
      <div className="mt-6 flex gap-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="flex-1 h-[88px] rounded-lg" />
        ))}
      </div>
      <Skeleton className="mt-8 h-10 rounded-lg" />
      <div className="mt-2 flex gap-2">
        <Skeleton className="flex-1 h-10 rounded-lg" />
        <Skeleton className="flex-1 h-10 rounded-lg" />
      </div>
      <Skeleton className="mt-8 h-48 rounded-lg" />
    </div>
  );
}

/* ── Helpers ── */

function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString();
}

function getStatusColor(status: string | null | undefined): string {
  switch (status) {
    case "screening": return "var(--warning)";
    case "interview": return "var(--info)";
    case "offer": return "var(--success)";
    case "rejected": return "var(--error)";
    default: return "var(--accent)";
  }
}

/* ── Main Dashboard Page ── */

function DashboardPage() {
  const { data: session } = useSession();
  const { resumes } = useResumeStore();
  const { jobs } = useJobStore();

  const { data: tierData } = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    retry: 1,
  });

  const { data: orpcResumes } = useQuery({ ...orpc.resume.list.queryOptions(), retry: 1 });
  const { data: orpcJobs } = useQuery({ ...orpc.job.list.queryOptions(), retry: 1 });
  const { data: orpcApplications } = useQuery({ ...orpc.application.list.queryOptions(), retry: 1 });
  const resumeCount = orpcResumes?.length ?? resumes.length;
  const jobCount = orpcJobs?.length ?? jobs.length;
  const applicationCount = orpcApplications?.length ?? 0;
  const hasMaster = orpcResumes?.some((r) => r.isMaster) ?? false;

  const tier = tierData?.tier ?? "free";
  const limits = tierData?.limits;
  const isFree = tier === "free";

  const firstName = session?.user?.name?.split(" ")[0] ?? "there";

  /* ── Activity items ── */
  const activityItems = React.useMemo(() => {
    const items: ActivityItem[] = [];

    for (const r of (orpcResumes ?? []).slice(0, 3)) {
      items.push({
        id: `resume-${r.id}`,
        title: r.title || "Untitled Resume",
        type: "resume" as const,
        status: "Updated",
        statusColor: "var(--success)",
        when: formatRelativeTime(r.updatedAt),
        href: `/resumes/${r.id}`,
      });
    }

    for (const j of (orpcJobs ?? []).slice(0, 3)) {
      items.push({
        id: `job-${j.id}`,
        title: `${j.title || "Untitled"} — ${j.company || "Unknown"}`,
        type: "job" as const,
        status: "Saved",
        statusColor: "var(--info)",
        when: formatRelativeTime(j.createdAt),
        href: `/jobs/${j.id}`,
      });
    }

    for (const a of (orpcApplications ?? []).slice(0, 3)) {
      items.push({
        id: `app-${a.id}`,
        title: a.jobTitle || "Application",
        type: "application" as const,
        status: a.status || "Applied",
        statusColor: getStatusColor(a.status),
        when: formatRelativeTime(a.appliedAt),
      });
    }

    return items
      .sort((a, b) => b.when.localeCompare(a.when))
      .slice(0, 5);
  }, [orpcResumes, orpcJobs, orpcApplications]);

  return (
    <div className="mx-auto max-w-5xl animate-fade-up">
      {/* ── Welcome Header (editorial) ── */}
      <div>
        <h1
          className="text-[22px] font-semibold"
          style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.5px", color: "var(--text)" }}
        >
          {m.dashboard_welcome({ name: firstName })}
        </h1>
        <p className="mt-1 text-[13px]" style={{ color: "var(--text-muted)" }}>
          {m.dashboard_stats_summary({
            resumes: String(resumeCount),
            resumePlural: resumeCount !== 1 ? "s" : "",
            jobs: String(jobCount),
            jobPlural: jobCount !== 1 ? "s" : "",
            applications: String(applicationCount),
            appPlural: applicationCount !== 1 ? "s" : "",
          })}
        </p>
      </div>

      {/* ── Stat Strip ── */}
      <div className="mt-6">
        <StatStrip
          items={[
            {
              label: m.dashboard_stat_resumes(),
              value: resumeCount,
              delta: isFree && limits ? `${resumeCount}/${limits.resumes}` : undefined,
              deltaColor: "var(--text-muted)",
              href: "/resumes",
            },
            {
              label: m.dashboard_stat_jobs(),
              value: jobCount,
              delta: isFree && limits ? `${jobCount}/${limits.jobTracking}` : undefined,
              deltaColor: "var(--text-muted)",
              href: "/jobs",
            },
            {
              label: m.dashboard_stat_applications(),
              value: applicationCount,
              href: "/applications",
            },
            {
              label: m.dashboard_stat_ai(),
              value: "\u2726",
              delta: m.common_ready(),
              deltaColor: "var(--accent)",
              accent: true,
            },
          ]}
        />
      </div>

      {/* ── Quick Actions (inline links) ── */}
      <div className="mt-8">
        <h2 className="section-label mb-3">{m.dashboard_quick_actions()}</h2>
        <div className="flex flex-col gap-2">
          <Link
            to="/resumes"
            className="flex items-center justify-between py-2 px-3 rounded-lg transition-colors group"
            style={{ background: "var(--accent-subtle)" }}
          >
            <div className="flex items-center gap-3">
              <span style={{ color: "var(--accent)" }}><SparklesIcon className="w-4 h-4" /></span>
              <div>
                <span className="text-[13px] font-medium" style={{ color: "var(--text)" }}>
                  {m.dashboard_action_ai_tailor()}
                </span>
                <span className="hidden sm:inline text-[12px] ml-2" style={{ color: "var(--text-muted)" }}>
                  {m.dashboard_action_ai_tailor_desc()}
                </span>
              </div>
            </div>
            <span className="text-[12px] transition-transform group-hover:translate-x-0.5" style={{ color: "var(--accent)" }}>&rarr;</span>
          </Link>
          <div className="flex gap-2">
            <Link
              to="/resumes"
              className="flex-1 flex items-center gap-2 py-2 px-3 rounded-lg border transition-colors"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}
            >
              <PlusIcon className="w-4 h-4" />
              <span className="text-[13px]">{m.dashboard_action_new_resume()}</span>
            </Link>
            <Link
              to="/jobs"
              className="flex-1 flex items-center gap-2 py-2 px-3 rounded-lg border transition-colors"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}
            >
              <BriefcaseIcon className="w-4 h-4" />
              <span className="text-[13px]">{m.dashboard_action_add_job()}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Next Steps (contextual — already restyled, keep as-is) ── */}
      <Suspense fallback={null}>
        <div className="mt-6">
          <NextSteps
            hasMaster={hasMaster}
            resumeCount={resumeCount}
            jobCount={jobCount}
            applicationCount={applicationCount}
          />
        </div>
      </Suspense>

      {/* ── Activity Table ── */}
      <div className="mt-10">
        <ActivityTable items={activityItems} viewAllHref="/analytics/activity" />
      </div>

      {/* ── Referral (subtle banner) ── */}
      <div className="mt-12 mb-4">
        <ReferralWidget />
      </div>
    </div>
  );
}
